import { api } from '../../../lib/api';
import { fmtDate } from '../../../lib/utils';
import { METHOD_LABEL, type IncassoRow, type PayMethod } from '../adminApi';

/**
 * Fatture e Spese (Amministrazione v2, lotto 5): le chiamate a
 * `/admin/spese/*` e le parole che le due pagine usano.
 *
 * Le parole stanno qui e non in adminApi.ts perché adminApi è di tutta la
 * sezione: queste seguono il glossario della proposta (§ 5) e la regola delle
 * etichette di SPEC.md (ogni etichetta nomina la cosa).
 */

// ── Tempo in parole ─────────────────────────────────────────────────────────

const DAY_MS = 86_400_000;

/** Giorni fra oggi e una data "YYYY-MM-DD" (negativo = passata). */
export function giorniDaOggi(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  const now = new Date();
  return Math.round((Date.UTC(y, m - 1, d) - Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())) / DAY_MS);
}

/** "6 mesi", "3 settimane", "4 giorni", "un anno": la durata detta come la si direbbe. */
export function durata(giorni: number): string {
  const g = Math.abs(giorni);
  if (g === 1) return 'un giorno';
  if (g < 14) return `${g} giorni`;
  if (g < 60) { const s = Math.floor(g / 7); return `${s} settimane`; }
  if (g < 365) { const m = Math.floor(g / 30); return m === 1 ? 'un mese' : `${m} mesi`; }
  const a = Math.floor(g / 365);
  return a === 1 ? 'più di un anno' : `più di ${a} anni`;
}

/**
 * Quando scade, in parole: "in ritardo da 6 mesi", "oggi", "domani",
 * "tra 12 giorni". La data esatta va nel tooltip (`title`), non qui.
 * `passato` è la parola per ciò che è scaduto: "in ritardo" per i clienti,
 * "scaduta" per le spese.
 */
export function quando(iso: string | null | undefined, passato: 'in ritardo' | 'scaduta' = 'in ritardo'): string {
  const d = giorniDaOggi(iso);
  if (d == null) return 'senza data';
  if (d === 0) return 'oggi';
  if (d === 1) return 'domani';
  if (d === -1) return passato === 'in ritardo' ? 'in ritardo da ieri' : 'scaduta ieri';
  if (d < 0) return `${passato} da ${durata(d)}`;
  return `tra ${durata(d)}`;
}

/** "il 24 settembre" (senza anno se è quest'anno): per le frasi. */
export function giornoInParole(iso: string | null | undefined): string {
  if (!iso) return '';
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return '';
  const dt = new Date(y, m - 1, d);
  const sameYear = y === new Date().getFullYear();
  return dt.toLocaleDateString('it-IT', { day: 'numeric', month: 'long', ...(sameYear ? {} : { year: 'numeric' }) });
}

/** Tooltip con la data esatta. */
export const dataEsatta = (iso: string | null | undefined, cosa = 'Scadenza') => (iso ? `${cosa}: ${fmtDate(iso)}` : undefined);

const PARTICELLE = new Set(['LA', 'LE', 'LO', 'IL', 'DI', 'DA', 'DEL', 'DELLA', 'E', 'ED']);

/**
 * Il nome come lo si dice in una frase: senza forma societaria, senza "di
 * Tizio Caio" in coda, e non tutto in maiuscolo.
 *   "Run Factory di Urso Salvatore" → "Run Factory"
 *   "C.E.D. ALICUOR S.R.L."          → "C.E.D. Alicuor"
 * Solo per le frasi: nelle righe resta il nome intero dell'anagrafica.
 */
export function nomeBreve(nome: string): string {
  // "Amazon Business EU S.a.r.l, Sede Secondaria": quello dopo la virgola non si dice.
  let n = nome.split(',')[0]!.trim();
  n = n.replace(/[\s,]+(S\.?A\.?R\.?L\.?|S\.?R\.?L\.?S?|S\.?P\.?A\.?|S\.?N\.?C\.?|S\.?A\.?S\.?|S\.?S\.?)\.?$/i, '').trim();
  const di = n.search(/\sdi\s/i);
  if (di > 3) n = n.slice(0, di);
  if (n === n.toUpperCase()) {
    n = n.split(/\s+/).map((w) => (w.includes('.') || (w.length <= 3 && !PARTICELLE.has(w)) ? w : w.charAt(0) + w.slice(1).toLowerCase())).join(' ');
  }
  return n;
}

// ── Parole: metodi di pagamento (glossario § 5, parole intere) ──────────────

/** La lista sta in @masamune/shared (ADMIN_PAY_METHOD_LABEL), la stessa di METHOD_LABEL: qui resta il nome per chi lo importa. */
export const METODO_LABEL: Record<PayMethod, string> = METHOD_LABEL;

/** Come è arrivato un movimento in banca, per le frasi dei suggerimenti. */
export function comeAddebitato(operationType: string | null | undefined): string {
  switch (operationType) {
    case 'card': return 'con la carta';
    case 'direct_debit': return 'con addebito automatico';
    case 'transfer': return 'con bonifico';
    case 'pay_later': return 'con il pagamento a rate di Qonto';
    default: return 'in banca';
  }
}

/** Metodo da registrare quando si conferma un movimento suggerito. */
export function metodoDaOperazione(operationType: string | null | undefined): PayMethod {
  switch (operationType) {
    case 'card': return 'CARTA';
    case 'direct_debit': return 'ADDEBITO';
    default: return 'BONIFICO';
  }
}

// ── Fatture › Fatture da incassare ──────────────────────────────────────────

export interface BonificoInBanca {
  txId: string;
  settledOn: string | null;
  amount: number;
  counterparty: string | null;
  label: string;
}

export type GruppoIncasso = 'RITARDO' | 'PROSSIMI' | 'AVANTI';

export interface RataDaIncassare {
  id: string;
  seq: number;
  ofTotal: number;
  invoiceId: string;
  invoiceNumber: string | null;
  invoiceTotal: number;
  issueDate: string | null;
  clientId: string;
  clientName: string;
  dueDate: string | null;
  amount: number;
  overdueDays: number;
  ficUrl: string | null;
  gruppo: GruppoIncasso;
  bonifico: BonificoInBanca | null;
}

interface ParteIncassi { amount: number; rate: number; fatture: number }

export interface DaIncassare {
  today: string;
  items: RataDaIncassare[];
  totals: {
    ritardo: ParteIncassi;
    prossimi: ParteIncassi;
    avanti: ParteIncassi;
    incassatoAnno: { year: number; amount: number; rate: number };
    bonificiInBanca: number;
  };
}

export function daIncassareQuery(includeHistory?: boolean) {
  return {
    queryKey: ['admin', 'spese', 'da-incassare', Boolean(includeHistory)] as const,
    queryFn: () => api.get<DaIncassare>(`/admin/spese/da-incassare${includeHistory ? '?includeHistory=true' : ''}`),
    staleTime: 15_000,
  };
}

/** La riga dell'Amministrazione che le azioni condivise si aspettano (label dei toast, ecc.). */
export function etichettaRata(r: Pick<RataDaIncassare, 'invoiceNumber' | 'clientName'> | IncassoRow): string {
  const numero = r.invoiceNumber ?? 's/n';
  const cliente = 'clientName' in r ? r.clientName : r.client.shortName;
  return `Fattura ${numero} · ${nomeBreve(cliente)}`;
}

/** "rata 1 di 3" · niente se la fattura ha una rata sola (glossario § 5). */
export function rataInParole(seq: number, ofTotal: number): string | null {
  if (ofTotal > 1) return `rata ${seq} di ${ofTotal}`;
  if (seq > 1) return `rata ${seq}`;
  return null;
}

// ── Spese › Spese da pagare ─────────────────────────────────────────────────

export type TipoSpesa = 'FORNITORE' | 'SPESA_FISSA' | 'CONTANTI';

export const TIPO_SPESA_LABEL: Record<TipoSpesa, string> = {
  FORNITORE: 'Fornitore',
  SPESA_FISSA: 'Spesa fissa',
  CONTANTI: 'Contanti',
};

export interface SuggerimentoSpesa {
  tipo: 'BANCA' | 'ADDEBITO';
  txId: string;
  settledOn: string | null;
  amount: number;
  counterparty: string | null;
  label: string;
  operationType: string | null;
}

export type GruppoSpesa = 'SCADUTE' | 'PROSSIMI' | 'AVANTI';

export interface SpesaRow {
  key: string;
  source: 'PAYABLE' | 'RECURRING' | 'CASH';
  tipo: TipoSpesa;
  id: string;
  refId: string;
  counterparty: string;
  description: string | null;
  number: string | null;
  seq: number | null;
  ofTotal: number | null;
  dueDate: string | null;
  amount: number;
  entity: string;
  method: PayMethod | null;
  category: string | null;
  overdueDays: number;
  gruppo: GruppoSpesa;
  suggerimento: SuggerimentoSpesa | null;
}

interface ParteSpese { amount: number; count: number; fornitori: number; fatture: number; fisse: number; contanti: number }

export interface DaPagare {
  today: string;
  entro: string;
  items: SpesaRow[];
  totals: {
    scadute: ParteSpese;
    prossimi: ParteSpese;
    avanti: ParteSpese;
    pagatoAnno: { year: number; amount: number; count: number };
    suggerimenti: { banca: number; addebiti: number };
  };
}

export function daPagareQuery(includeHistory?: boolean) {
  return {
    queryKey: ['admin', 'spese', 'da-pagare', Boolean(includeHistory)] as const,
    queryFn: () => api.get<DaPagare>(`/admin/spese/da-pagare${includeHistory ? '?includeHistory=true' : ''}`),
    staleTime: 15_000,
  };
}

// ── Spese › Spese fisse ─────────────────────────────────────────────────────

export interface SpesaFissa {
  id: string;
  name: string;
  counterparty: string | null;
  amountNet: number;
  vatRate: number;
  amountGross: number;
  cycle: 'MONTHLY' | 'BIMONTHLY' | 'QUARTERLY' | 'YEARLY';
  dayOfMonth: number | null;
  startDate: string | null;
  endDate: string | null;
  nextDueDate: string | null;
  active: boolean;
  category: string;
  method: PayMethod | null;
  matchRule: string | null;
  entity: string;
  note: string | null;
  alMese: number;
  inVigore: boolean;
  auto: boolean;
  nextOccurrences: string[];
}

export interface AutoARate {
  payableId: string;
  vendorName: string;
  number: string | null;
  issueDate: string | null;
  financed: number;
  entity: string;
  recurring: { id: string; name: string; counterparty: string | null; monthly: number; active: boolean; nextDueDate: string | null; method: PayMethod | null; inVigore: boolean } | null;
}

export interface RateMancanti {
  recurringId: string;
  nome: string;
  addebiti: number;
  totaleAddebiti: number;
  dallaBanca: string | null;
  dalGestionale: string | null;
}

export interface SpeseFisseData {
  today: string;
  items: SpesaFissa[];
  groups: Array<{ category: string; count: number; monthly: number }>;
  auto: AutoARate[];
  rateMancanti: RateMancanti[];
  totals: { count: number; monthly: number; autoMonthly: number; ferme: number };
}

export const speseFisseQuery = {
  queryKey: ['admin', 'spese', 'fisse'] as const,
  queryFn: () => api.get<SpeseFisseData>('/admin/spese/fisse'),
  staleTime: 30_000,
};

/** "al mese", "ogni due mesi"… detto dopo un importo. */
export const OGNI_QUANTO: Record<SpesaFissa['cycle'], string> = {
  MONTHLY: 'al mese',
  BIMONTHLY: 'ogni due mesi',
  QUARTERLY: 'ogni tre mesi',
  YEARLY: "all'anno",
};

/** Nomi dei gruppi di spese fisse: il sostantivo intero (glossario, test delle etichette). */
export const CATEGORIA_SPESA_LABEL: Record<string, string> = {
  SAAS: 'Software e abbonamenti online',
  AFFITTO: 'Affitto',
  UTENZE: 'Telefono, internet ed energia',
  AUTO: 'Auto',
  CONSULENTI: 'Consulenti',
  COLLABORATORI: 'Collaboratori',
  FORNITORI: 'Fornitori',
  ALTRO: 'Assicurazioni, banca e altre spese',
};

// ── Spese › Spese già pagate ────────────────────────────────────────────────

export interface SpesaPagata {
  key: string;
  source: 'PAYABLE' | 'CASH';
  tipo: TipoSpesa;
  id: string;
  refId: string;
  counterparty: string;
  description: string | null;
  number: string | null;
  seq: number | null;
  paidDate: string | null;
  dueDate: string | null;
  amount: number;
  method: PayMethod | null;
  entity: string;
  bankTransactionId: string | null;
  localPaidAt: string | null;
}

export interface SpesePagateData {
  items: SpesaPagata[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  mesi: Array<{ month: string; count: number }>;
  totals: { amount: number };
}

export function spesePagateQuery(p: { search?: string; month?: string; includeHistory?: boolean; page: number; pageSize?: number }) {
  const sp = new URLSearchParams();
  if (p.search?.trim()) sp.set('search', p.search.trim());
  if (p.month) sp.set('month', p.month);
  if (p.includeHistory) sp.set('includeHistory', 'true');
  sp.set('page', String(p.page));
  sp.set('pageSize', String(p.pageSize ?? 25));
  return {
    queryKey: ['admin', 'spese', 'pagate', p] as const,
    queryFn: () => api.get<SpesePagateData>(`/admin/spese/pagate?${sp.toString()}`),
    staleTime: 15_000,
  };
}

/** "settembre 2026" da "2026-09". */
export function meseInParole(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  if (!y || !m) return ym;
  return new Date(y, m - 1, 1).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
}
